import React, { useState, useEffect, useMemo } from 'react';
import { User, AdminStats, GeneratedCredential } from '../../types';
import LoadingSpinner from '../ui/LoadingSpinner';
import AdminStatCard from '../ui/AdminStatCard';
import ResetPasswordConfirmationModal from '../modals/ResetPasswordConfirmationModal';
import { api } from '../../utils/api';

interface AdminDashboardProps {
  user: User;
}

const initials = (a?: string, b?: string) => `${(a || '?')[0]}${(b || '')[0] || ''}`.toUpperCase();

const Avatar: React.FC<{ a?: string; b?: string; color: string }> = ({ a, b, color }) => (
  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${color}`}>
    {initials(a, b)}
  </div>
);

const EmptyState: React.FC<{ icon: string; message: string }> = ({ icon, message }) => (
  <div className="py-12 text-center">
    <div className="text-4xl mb-2">{icon}</div>
    <p className="text-gray-500">{message}</p>
  </div>
);

const AdminDashboard: React.FC<AdminDashboardProps> = ({ user }) => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [pendingTeachers, setPendingTeachers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'teachers' | 'students' | 'parents' | 'approvals'>('overview');
  const [resetCredential, setResetCredential] = useState<GeneratedCredential | null>(null);

  const fetchData = async () => {
      if (!user) return;
      setLoading(true);
      setError(null);
      try {
        const [statsData, usersData, pendingData] = await Promise.all([
          api.get<AdminStats>(`/api/v1/admin/${user.school_id}/stats`),
          api.get<User[]>(`/api/v1/admin/${user.school_id}/users`),
          api.get<User[]>(`/api/v1/admin/${user.school_id}/pending-teachers`)
        ]);

        setStats(statsData);
        setAllUsers(usersData);
        setPendingTeachers(pendingData);
      } catch (e) {
        if (e instanceof Error) setError(e.message);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchData();
  }, [user]);

  const { teachers, students, parents } = useMemo(() => {
    return {
      teachers: allUsers.filter(u => u.role === 'teacher'),
      students: allUsers.filter(u => u.role === 'student'),
      parents: allUsers.filter(u => u.role === 'parent'),
    };
  }, [allUsers]);


  const handleApproveTeacher = async (teacherId: string) => {
    setError(null);
    try {
        await api.post('/api/v1/admin/approve-teacher', { teacher_id: teacherId });
        await fetchData(); // Refetch all data to ensure consistency
    } catch (err) {
        if (err instanceof Error) setError(err.message);
    }
  };

  const handleDeclineTeacher = async (teacherId: string) => {
    setError(null);
     try {
        await api.post('/api/v1/admin/decline-teacher', { teacher_id: teacherId });
        setPendingTeachers(prev => prev.filter(t => t.user_id !== teacherId));
    } catch (err) {
        if (err instanceof Error) setError(err.message);
    }
  };

  const handleResetStaffPassword = async (userId: string) => {
    setError(null);
    if (!window.confirm("Are you sure you want to reset this user's password? This action cannot be undone.")) {
        return;
    }
    try {
        const data = await api.post<GeneratedCredential>('/api/v1/admin/reset-staff-password', { userId });
        setResetCredential(data);
    } catch(err) {
        if (err instanceof Error) setError(err.message);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm("Are you sure you want to permanently delete this user? This will remove all their associated data and cannot be undone.")) return;
    setError(null);
    try {
        await api.delete(`/api/v1/admin/users/${userId}`);
        setAllUsers(prev => prev.filter(u => u.user_id !== userId));
    } catch(err) {
        if (err instanceof Error) setError(err.message);
    }
  };


  if (loading) return <LoadingSpinner message="Loading admin dashboard..." />;

  const formatNum = (num?: number) => num ? Number(num).toLocaleString() : '0';
  const formatAvg = (num?: number) => num ? Number(num).toFixed(1) : '0.0';

  const renderOverview = () => {
    if(!stats) return <p>No stats found.</p>;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <AdminStatCard icon="🎓" color="blue" label="Total Students" value={formatNum(stats.users?.total_students)} description="At your school" />
            <AdminStatCard icon="🍎" color="green" label="Total Teachers" value={formatNum(stats.users?.total_teachers)} description="At your school" />
            <AdminStatCard icon="⚡" color="purple" label="School Avg. WPM" value={formatAvg(stats.progress?.school_avg_wpm)} description="School-wide average" />
            <AdminStatCard icon="🎯" color="orange" label="School Avg. Accuracy" value={`${formatAvg(stats.progress?.school_avg_accuracy)}%`} description="School-wide average" />
        </div>
    )
  }

  const UserTable: React.FC<{users: User[], title: string, icon: string, avatarColor: string, isStudents?: boolean}> = ({ users, title, icon, avatarColor, isStudents }) => (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
            <span className="text-xl">{icon}</span>
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            <span className="ml-auto text-sm text-gray-400">{users.length} total</span>
        </div>
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{isStudents ? 'Username' : 'Name'}</th>
                    {!isStudents && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {users.length > 0 ? users.map((u) => (
                    <tr key={u.user_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div className="flex items-center gap-3">
                                <Avatar a={u.first_name} b={u.last_name} color={avatarColor} />
                                {isStudents ? (u.username || u.first_name) : `${u.first_name} ${u.last_name}`}
                            </div>
                        </td>
                        {!isStudents && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.email}</td>}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                            <button onClick={() => handleResetStaffPassword(u.user_id)} className="text-blue-600 hover:text-blue-900">Reset Pass</button>
                            <button onClick={() => handleDeleteUser(u.user_id)} className="text-red-600 hover:text-red-900">Delete</button>
                        </td>
                    </tr>
                )) : (
                    <tr><td colSpan={isStudents ? 2 : 3} className="p-0"><EmptyState icon={icon} message={`No ${title.toLowerCase()} found for this role.`} /></td></tr>
                )}
                </tbody>
            </table>
        </div>
    </div>
  );

  const renderApprovals = () => (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
            <span className="text-xl">✅</span>
            <h3 className="text-lg font-semibold text-gray-800">Pending Teacher Approvals</h3>
        </div>
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {pendingTeachers.length > 0 ? pendingTeachers.map((t) => (
                    <tr key={t.user_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div className="flex items-center gap-3">
                                <Avatar a={t.first_name} b={t.last_name} color="bg-amber-500" />
                                {t.first_name} {t.last_name}
                            </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                            <button onClick={() => handleApproveTeacher(t.user_id)} className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 font-semibold">Approve</button>
                            <button onClick={() => handleDeclineTeacher(t.user_id)} className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 font-semibold">Decline</button>
                        </td>
                    </tr>
                )) : (
                    <tr><td colSpan={3} className="p-0"><EmptyState icon="🎉" message="No pending teacher approvals -- you're all caught up!" /></td></tr>
                )}
                </tbody>
            </table>
        </div>
    </div>
  )

  const TABS: { key: typeof tab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: '📊' },
    { key: 'teachers', label: 'Teachers', icon: '🍎' },
    { key: 'students', label: 'Students', icon: '🎓' },
    { key: 'parents', label: 'Parents', icon: '👪' },
    { key: 'approvals', label: 'Approvals', icon: '✅' },
  ];

  return (
    <div className="w-full max-w-7xl">
      {resetCredential && <ResetPasswordConfirmationModal credential={resetCredential} onClose={() => setResetCredential(null)} />}

      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-3xl shadow-md">🏫</div>
        <div>
            <h2 className="text-3xl font-bold text-gray-800">School Admin Dashboard</h2>
            <span className="inline-block mt-1 px-3 py-0.5 bg-blue-50 text-blue-700 rounded-full text-sm font-semibold">{user.school_name}</span>
        </div>
      </div>

      {error && <div className="text-red-500 bg-red-100 p-3 rounded-md mb-4 text-center">{error}</div>}

       <div className="flex border-b border-gray-300 mb-6 flex-wrap">
        {TABS.map(t => (
            <button
                key={t.key}
                className={`py-2 px-6 font-semibold flex items-center gap-1.5 relative ${tab === t.key ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setTab(t.key)}
            >
                <span>{t.icon}</span> {t.label}
                {t.key === 'approvals' && pendingTeachers.length > 0 && (
                    <span className="absolute top-1 right-1 flex h-5 w-5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 text-white text-xs items-center justify-center">{pendingTeachers.length}</span></span>
                )}
            </button>
        ))}
      </div>

      {tab === 'overview' && renderOverview()}
      {tab === 'teachers' && <UserTable users={teachers} title="School Teachers" icon="🍎" avatarColor="bg-green-500" />}
      {tab === 'students' && <UserTable users={students} title="School Students" icon="🎓" avatarColor="bg-blue-500" isStudents />}
      {tab === 'parents' && <UserTable users={parents} title="School Parents" icon="👪" avatarColor="bg-purple-500" />}
      {tab === 'approvals' && renderApprovals()}

    </div>
  );
};

export default AdminDashboard;
